import { useState } from "react";
import { useRouteContext } from "@tanstack/react-router";
import { useLeaData } from "@/queries";

type ChildRecordType = "CHLD" | "ENRL" | "CLEN" | "FAMI" | "LANG" | "SLNG";
type StaffRecordType = "STFF" | "SASS" | "SCLR";
type RecordType = ChildRecordType | StaffRecordType | "CLRM" | "ATTN";
type ViewMode = "summary" | "detail";

export type RelatedRecord = {
  recordType: string;
  recordId: string;
  status: "Primary" | "Secondary" | "Missing" | "Exists";
  count: number;
  records: any[];
};

type UseRelatedRecordsTabProps = {
  entityId: string | undefined;
  currentRecordType: RecordType | string;
  csppid: string | undefined;
  secondaryRecordType?: string;
  onDetailViewChange?: (isDetailView: boolean, onBack?: () => void) => void;
};

const isChildRecordType = (
  recordType: string
): recordType is ChildRecordType => {
  return ["CHLD", "ENRL", "CLEN", "FAMI", "LANG", "SLNG"].includes(recordType);
};

const isStaffRecordType = (
  recordType: string
): recordType is StaffRecordType => {
  return ["STFF", "SASS", "SCLR"].includes(recordType);
};

export function useRelatedRecordsTab({
  entityId,
  currentRecordType,
  csppid,
  secondaryRecordType,
  onDetailViewChange,
}: UseRelatedRecordsTabProps) {
  const [viewMode, setViewMode] = useState<ViewMode>("summary");
  const [selectedRecordType, setSelectedRecordType] = useState<string | null>(
    null
  );
  const [selectedRecords, setSelectedRecords] = useState<any[]>([]);

  const leaContext = useRouteContext({ from: "/$leaid" });
  const { data: leaData } = useLeaData(leaContext.currentUserOrg.esiIdPath);

  const isStaffContext = isStaffRecordType(currentRecordType);

  const getRecordStatus = (
    recordType: string,
    hasRecords: boolean
  ): "Primary" | "Secondary" | "Missing" | "Exists" => {
    if (recordType === currentRecordType) return "Primary";
    if (recordType === secondaryRecordType) return "Secondary";
    if (!hasRecords) return "Missing";
    return "Exists";
  };

  const buildChildRelatedRecords = (): RelatedRecord[] => {
    if (!csppid) return [];

    const chldRecord = leaData?.chld?.find((c) => c.CSPPID === csppid);
    const records: RelatedRecord[] = [];

    records.push({
      recordType: "CHLD",
      recordId: chldRecord ? `CSPPID — ${csppid}` : "Not found",
      status: getRecordStatus("CHLD", !!chldRecord),
      count: chldRecord ? 1 : 0,
      records: chldRecord ? [chldRecord] : [],
    });

    const famiRecords = (chldRecord as any)?.FamilyRecords ?? [];
    records.push({
      recordType: "FAMI",
      recordId:
        famiRecords.length > 0
          ? `${famiRecords.length} record(s)`
          : "Not created",
      status: getRecordStatus("FAMI", famiRecords.length > 0),
      count: famiRecords.length,
      records: famiRecords,
    });

    const langRecords = (chldRecord as any)?.LangRecords ?? [];
    records.push({
      recordType: "LANG",
      recordId:
        langRecords.length > 0
          ? `${langRecords.length} record(s)`
          : "Not created",
      status: getRecordStatus("LANG", langRecords.length > 0),
      count: langRecords.length,
      records: langRecords,
    });

    const slngRecords = (chldRecord as any)?.SLngRecords ?? [];
    records.push({
      recordType: "SLNG",
      recordId:
        slngRecords.length > 0
          ? `${slngRecords.length} record(s)`
          : "Not created",
      status: getRecordStatus("SLNG", slngRecords.length > 0),
      count: slngRecords.length,
      records: slngRecords,
    });

    const enrlRecords = (chldRecord as any)?.Enrollments ?? [];
    records.push({
      recordType: "ENRL",
      recordId:
        enrlRecords.length > 0
          ? `${enrlRecords.length} record(s)`
          : "Not created",
      status: getRecordStatus("ENRL", enrlRecords.length > 0),
      count: enrlRecords.length,
      records: enrlRecords,
    });

    const clenRecords = (chldRecord as any)?.ClassroomAssignments ?? [];
    records.push({
      recordType: "CLEN",
      recordId:
        clenRecords.length > 0
          ? `${clenRecords.length} record(s)`
          : "Not created",
      status: getRecordStatus("CLEN", clenRecords.length > 0),
      count: clenRecords.length,
      records: clenRecords,
    });

    return records;
  };

  const buildStaffRelatedRecords = (): RelatedRecord[] => {
    if (!entityId) return [];

    const stffRecord = leaData?.stff?.find(
      (s) => String(s.LocalStaffId) === String(entityId)
    );
    const records: RelatedRecord[] = [];

    records.push({
      recordType: "STFF",
      recordId: stffRecord ? `LocalStaffId — ${entityId}` : "Not found",
      status: getRecordStatus("STFF", !!stffRecord),
      count: stffRecord ? 1 : 0,
      records: stffRecord ? [stffRecord] : [],
    });

    const sassRecords = (stffRecord as any)?.StaffAssignments ?? [];
    records.push({
      recordType: "SASS",
      recordId:
        sassRecords.length > 0
          ? `${sassRecords.length} record(s)`
          : "Not created",
      status: getRecordStatus("SASS", sassRecords.length > 0),
      count: sassRecords.length,
      records: sassRecords,
    });

    const sclrRecords = (stffRecord as any)?.ClassroomAssignments ?? [];
    records.push({
      recordType: "SCLR",
      recordId:
        sclrRecords.length > 0
          ? `${sclrRecords.length} record(s)`
          : "Not created",
      status: getRecordStatus("SCLR", sclrRecords.length > 0),
      count: sclrRecords.length,
      records: sclrRecords,
    });

    return records;
  };

  const relatedRecords = isChildRecordType(currentRecordType)
    ? buildChildRelatedRecords()
    : isStaffRecordType(currentRecordType)
      ? buildStaffRelatedRecords()
      : [];

  const handleViewRecords = (recordType: string, records: any[]) => {
    setSelectedRecordType(recordType);
    setSelectedRecords(records);
    setViewMode("detail");

    if (onDetailViewChange) {
      onDetailViewChange(true, handleBack);
    }
  };

  const handleBack = () => {
    setViewMode("summary");
    setSelectedRecordType(null);
    setSelectedRecords([]);

    if (onDetailViewChange) {
      onDetailViewChange(false);
    }
  };

  const formatRecordId = (recordType: string, index: number): string => {
    return `${recordType}-${String(index + 1).padStart(3, "0")}`;
  };

  return {
    viewMode,
    selectedRecordType,
    selectedRecords,
    relatedRecords,
    isStaffContext,

    handleViewRecords,
    handleBack,

    formatRecordId,

    entityId,
    csppid,
  };
}
